import { useEffect, useRef, useState } from 'react';
import { Button as AriaButton, Menu, MenuItem, MenuTrigger, Popover } from 'react-aria-components';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'clsx';
import { formatDate, Tooltip } from '@repo/design-system-kit';
import { IconDotsOutlined, IconShortcutCreateInvoiceOutlined } from '@repo/monochrome-icons';
import { CopyToClipboardButton } from '@repo/shared-kit/components';
import { IconApplePay } from '../../../assets/icons/icon-apple-pay';
import { IconCreditCard } from '../../../assets/icons/icon-credit-card';
import { IconIdeal } from '../../../assets/icons/icon-ideal';
import { IconPaypal } from '../../../assets/icons/icon-paypal';
import type { StandalonePaymentMethod } from '../../types';
import styles from './payment.module.css';

export interface PaymentProps {
  id: string;
  paymentMethod: StandalonePaymentMethod;
  userEmail: string;
  createdAt: string;
  onClickCreateInvoice?: () => void;
}

const methodIcons: Record<StandalonePaymentMethod, React.ReactNode> = {
  credit_card: <IconCreditCard />,
  apple_pay: <IconApplePay />,
  paypal: <IconPaypal />,
  ideal: <IconIdeal />,
};

export function Payment({
  payment,
  onClickCreateInvoice,
}: {
  payment: PaymentProps;
  onClickCreateInvoice?: () => void;
}): React.ReactElement {
  const { formatMessage, locale } = useIntl();
  const emailContainerRef = useRef<HTMLDivElement>(null);
  const [isTooltipDisabled, setIsTooltipDisabled] = useState(true);

  useEffect(() => {
    const container = emailContainerRef.current;
    if (container) {
      const spanElement = container.querySelector('span');
      if (spanElement) {
        setIsTooltipDisabled(spanElement.scrollWidth <= spanElement.clientWidth);
      }
    }
  }, [payment.userEmail]);

  const ariaLabel = `${formatMessage({ id: 'aria.copy_to_clipboard.generic' })}: ${payment.userEmail}`;

  return (
    <div className={styles.payment} data-test-related-payment-item key={payment.id}>
      <div className={styles.method}>{methodIcons[payment.paymentMethod]}</div>
      <div className={styles.info}>
        <div ref={emailContainerRef}>
          <Tooltip isDisabled={isTooltipDisabled} label={payment.userEmail} placement="top">
            <CopyToClipboardButton
              aria-label={ariaLabel}
              className={styles.copyBtn}
              clipboardText={payment.userEmail}
              hasIcon
              mode="clipboard"
              toastLabel={formatMessage({ id: 'payment-links.toasts.email.copy' })}
              toastType="info"
            >
              {payment.userEmail}
            </CopyToClipboardButton>
          </Tooltip>
        </div>
        <div className={cx(styles.date, 'body-2')}>
          {formatDate({ date: payment.createdAt, locale, token: 'date-year-s' })}
        </div>
      </div>
      <MenuTrigger>
        <AriaButton
          aria-hidden="true"
          aria-label={formatMessage({
            id: 'payment-links.sidebar.related-payments.context-menu.create-invoice',
          })}
          className={styles.trigger}
          data-testid="trigger"
        >
          <IconDotsOutlined />
        </AriaButton>
        <Popover
          UNSTABLE_portalContainer={document.getElementById('ember-testing') ?? document.body}
          className={styles.popover}
          maxHeight={200}
          offset={8}
          placement="bottom right"
        >
          <Tooltip
            buttonProps={{
              className: styles['menu-button'],
            }}
            label={formatMessage({
              id: 'payment-links.sidebar.related-payments.context-menu.coming-soon',
            })}
            needsButton
            offset={16}
            placement="top"
          >
            <Menu>
              <MenuItem
                className={cx('body-1', styles['list-item'])}
                data-test-create-invoice
                onAction={() => {
                  if (onClickCreateInvoice) {
                    onClickCreateInvoice();
                  }
                }}
              >
                <IconShortcutCreateInvoiceOutlined />

                <span>
                  <FormattedMessage id="payment-links.sidebar.related-payments.context-menu.create-invoice" />
                </span>
              </MenuItem>
            </Menu>
          </Tooltip>
        </Popover>
      </MenuTrigger>
    </div>
  );
}
